const apiBase = import.meta.env.DEV ? "https://localhost:7284/" : "";
const BaseURL = `${apiBase}api/`;
const RETRY_STATUSES = new Set([401, 403, 419, 440]);

let sessionPromise: Promise<void> | null = null;
let sessionReady = document.cookie.includes("breeze_sid=");

export function getLastImageUrl(cameraId: string, addTime = true): string {
    return `${BaseURL}image/${cameraId}/last${addTime ? `?=${new Date().getTime()}` : ""}`;
}

export async function getImageTimestamp(cameraId: string): Promise<string | null> {
    const headers = await apiHead(getLastImageUrl(cameraId));
    const header = headers?.get("Last-Modified") ?? null;
    return header ? new Date(header).toLocaleString() : null;
}

async function establishSession() {
    if (sessionReady && document.cookie.includes("breeze_sid=")) return;
    if (!sessionPromise) {
        sessionPromise = (async () => {
            const r = await fetch(BaseURL + "session", { credentials: "include" });
            if (!r.ok && r.status !== 204) {
                const msg = await r.text().catch(() => "");
                throw new Error(`/api/session failed ${r.status}: ${msg}`);
            }
            sessionReady = true;
        })().finally(() => { sessionPromise = null; });
    }
    return sessionPromise;
}

async function apiFetch(input: RequestInfo | URL, init: RequestInit) {
    let res = await fetch(input, init);
    if (RETRY_STATUSES.has(res.status)) {
        sessionReady = false;
        await establishSession();
        res = await fetch(input, init);
    }
    return res;
}

export async function apiGet<T>(url: string): Promise<T | null> {
    try {
        const res = await apiFetch(BaseURL + url, {
            method: "GET",
            headers: { "Accept": "application/json" },
            cache: "no-store",
            credentials: "include"
        });
        if (!res.ok) throw new Error(`API GET ${url} failed: ${res.status}`);
        return await res.json() as T;
    } catch (error) {
        console.error("API Failed", error);
        return null;
    }
}

export async function apiHead(url: string, addBaseUrl = false): Promise<Headers | null> {
    try {
        const full = addBaseUrl ? BaseURL + url : url;
        const res = await apiFetch(full, {
            mode: "cors",
            method: "HEAD",
            cache: "no-store",
            credentials: "include"
        });
        if (!res.ok) throw new Error(`API HEAD ${full} failed: ${res.status}`);
        return res.headers;
    } catch (error) {
        console.error("API Failed", error);
        return null;
    }
}

export const getCookie = (n: string) => document.cookie.split("; ").find(r => r.startsWith(n + "="))?.split("=")[1] ?? "";

export async function getWSTicket() {
    if (!getCookie("breeze_x")) {
        await establishSession();
    }
    let csrf = getCookie("breeze_x");
    if (!csrf) throw new Error("No valid CSRF token after session establish");
    const r1 = await fetch(apiBase + `ws-ticket`, {
        method: "POST",
        credentials: "include",
        headers: { "X-Breeze": csrf }
    });
    if (RETRY_STATUSES.has(r1.status)) {
        sessionReady = false;
        await establishSession();
        csrf = getCookie("breeze_x");
        if (!csrf) throw new Error("No valid CSRF token after re-establish");
        const r2 = await fetch(apiBase + `ws-ticket`, {
            method: "POST",
            credentials: "include",
            headers: { "X-Breeze": csrf }
        });
        if (!r2.ok) {
            const msg = await r2.text().catch(() => "");
            throw new Error(`/ws-ticket failed ${r2.status}: ${msg}`);
        }
        return r2.text();
    }
    if (!r1.ok) {
        const msg = await r1.text().catch(() => "");
        throw new Error(`/ws-ticket failed ${r1.status}: ${msg}`);
    }
    return r1.text();
}

window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && !document.cookie.includes("breeze_sid=")) {
        sessionReady = false;
        establishSession().catch(() => { });
    }
});
window.addEventListener("focus", () => {
    if (!document.cookie.includes("breeze_sid=")) {
        sessionReady = false;
        establishSession().catch(() => { });
    }
});
